import { GoogleMap, Polygon } from '@react-google-maps/api';
import { App, Checkbox, DatePicker, Radio, Select, Tag, Tooltip } from 'antd';
import type { Dayjs } from 'dayjs';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { AppModal } from '@/components/ui/AppModal';
import type {
  GetDriversDistrictListByIdQuery,
  GetWeekdayWindowForSlotQuery,
  GetWeekdayWindowsAllQuery,
} from '@/generated/graphql/graphql';
import type { Weekday_Window } from '@/generated/graphql-types';
import dayjs from '@/utils/dayjs-instance';
import { formatError, logError } from '@/utils/formatError';
import {
  useGetDriverDistricts,
  useGetDriversDistrictListById,
  useGetWeekdayWindowsAll,
  useGetWeekdayWindowsForSlot,
  useInsertDriverDistrictOld,
  useInsertWeekdayWindowDistricts,
  useInsertWeekdayWindows,
} from '../../query/calendarGrid.hooks';
import { ScheduleModal } from '../ScheduleModal';
import type { RangeValue } from './CalendarGrid';
import { DistrictInsertType, useCalendarState } from './useCalendarState';

interface AddDistrictModalProps {
  open: boolean;
  close: () => void;
  departmentId: number;
  modalTitle: string;
}

export interface IPolygon {
  name: string | null | undefined;
  coordinates: { lat: number; lng: number }[];
}

const disabledPastDates = (current: Dayjs) => {
  return current?.isBefore(dayjs().startOf('day'));
};

const getWeekdaysInDateRange = (startDate: Dayjs, endDate: Dayjs): number[] => {
  const weekdays = new Set<number>();
  let current = startDate.startOf('day');
  const end = endDate.startOf('day');

  while (current.isSameOrBefore(end, 'day')) {
    weekdays.add(current.isoWeekday());
    current = current.add(1, 'day');
  }

  return Array.from(weekdays);
};

export const AddDistrictModal: FC<AddDistrictModalProps> = ({
  open,
  close,
  departmentId,
  modalTitle,
}) => {
  const { message } = App.useApp();
  const { state, setSelectedDistricts, setDistrictInsertType } = useCalendarState();
  const isLoaded = Boolean(window.google?.maps);
  const mapRef = useRef<google.maps.Map | null>(null);
  const insertDriverDistrictOld = useInsertDriverDistrictOld();
  const [isTempDistrict, setIsTempDistrict] = useState(false);
  const [tempDateRange, setTempDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const handleDateRangeChange = (dates: RangeValue) => {
    if (dates?.[0] && dates[1]) {
      setTempDateRange([dates[0], dates[1]]);
    } else {
      setTempDateRange(null);
    }
  };

  const reset = () => {
    setTempDateRange(null);
    setIsTempDistrict(false);
    setDistrictInsertType(DistrictInsertType.SINGLE);
  };

  const {
    mutateAsync: insertWeekdayWindowDistricts,
    isPending: insertWeekdayWindowDistrictsLoading,
  } = useInsertWeekdayWindowDistricts();

  const { mutateAsync: insertWeekdayWindows, isPending: insertWeekdayWindowsLoading } =
    useInsertWeekdayWindows();

  const { refetch: weekdayWindowsForSlotRefetch } = useGetWeekdayWindowsForSlot({
    driverWindowId: state.selectedSlotId ?? 0,
  });

  const { refetch: weekDayWindowsAllRefetch } = useGetWeekdayWindowsAll({
    driverId: state.selectedDriverId ?? '',
  });

  const { data: driversDistrictsData, isLoading: driverDistrictsDataLoading } =
    useGetDriverDistricts({ departmentId });

  const [selectedPolygons, setSelectedPolygons] = useState<IPolygon[] | null>(null);

  useEffect(() => {
    if (isTempDistrict) {
      setDistrictInsertType(DistrictInsertType.WEEK);
    }
    // eslint-disable-next-line
  }, [isTempDistrict, setDistrictInsertType]);

  const handleDistrictSelect = (selectedDistricts: string[]) => {
    setSelectedDistricts(selectedDistricts);
  };

  useEffect(() => {
    const driverDistricts = driversDistrictsData?.district;
    if (state.selectedDistricts && driverDistricts) {
      const matchingPolygons = driverDistricts
        .filter(
          (d) => state.selectedDistricts?.includes(d.id) && d.districts_coordinates.length > 0
        )
        .map((d) => ({
          name: d.name,
          coordinates: d.districts_coordinates.map((coord) => ({
            lat: coord.latitude,
            lng: coord.longitude,
          })),
        })) as IPolygon[];

      setSelectedPolygons(matchingPolygons.length > 0 ? matchingPolygons : null);

      if (matchingPolygons.length > 0 && window.google?.maps) {
        const bounds = new window.google.maps.LatLngBounds();

        matchingPolygons.forEach((polygon) => {
          polygon.coordinates.forEach(({ lat, lng }) => {
            bounds.extend({ lat, lng });
          });
        });
        setTimeout(() => {
          mapRef.current?.fitBounds(bounds);
        }, 300);
      }
    }
  }, [state.selectedDistricts, driversDistrictsData]);

  const getDriverDistrictsListById = useGetDriversDistrictListById({
    driverId: state.selectedDriverId ?? '',
  });

  const createDistrictObjects = useCallback(
    (
      weekdayWindowIds: number[],
      districtIds: string[],
      tempDateFrom: string | null,
      tempDateTo: string | null
    ) => {
      return weekdayWindowIds.flatMap((weekdayWindowId) =>
        districtIds.map((districtId) => ({
          weekdayWindowId,
          districtId,
          tempDateFrom,
          tempDateTo,
        }))
      );
    },
    []
  );

  const ensureWeekdayWindowExists = useCallback(
    async (slotId: number, weekday: number): Promise<number | undefined> => {
      const slot = state.timeSlots.find((s) => s.id === slotId);
      const existingWindow = slot?.weekday_windows.find((ww) => ww.weekday === weekday);
      if (existingWindow?.id) {
        return typeof existingWindow.id === 'string'
          ? parseInt(existingWindow.id, 10)
          : existingWindow.id;
      }

      const newWeekdayWindow = await insertWeekdayWindows({
        objects: [{ driverWindowId: slotId, weekday }],
      });
      return newWeekdayWindow.insert_weekday_window?.returning[0]?.id;
    },
    [state.timeSlots, insertWeekdayWindows]
  );

  const findMissingDays = useCallback(
    (
      weekdayWindows:
        | GetWeekdayWindowForSlotQuery['weekday_window']
        | GetWeekdayWindowsAllQuery['weekday_window']
        | undefined
    ) => {
      const missingDays: number[] = [];
      for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
        const hasWindow = weekdayWindows?.some((el) => el.weekday === dayIndex + 1);
        if (!hasWindow) {
          missingDays.push(dayIndex + 1);
        }
      }
      return missingDays;
    },
    []
  );

  const ensureWeekdayWindowsForDays = useCallback(
    async (
      slotId: number,
      existingWindows: GetWeekdayWindowForSlotQuery | GetWeekdayWindowsAllQuery | undefined
    ) => {
      const missingDays = findMissingDays(existingWindows?.weekday_window);
      if (missingDays.length === 0) {
        return existingWindows;
      }

      const newWeekdayWindows = await insertWeekdayWindows({
        objects: missingDays.map((day: number) => ({
          driverWindowId: slotId,
          weekday: day,
        })),
      });
      const newWindows = newWeekdayWindows.insert_weekday_window?.returning ?? [];
      const existingWeekdayWindows = existingWindows?.weekday_window ?? [];

      return {
        ...(existingWindows ?? {}),
        weekday_window: [...existingWeekdayWindows, ...newWindows] as NonNullable<
          GetWeekdayWindowForSlotQuery['weekday_window']
        >,
      } as GetWeekdayWindowForSlotQuery | GetWeekdayWindowsAllQuery;
    },
    [findMissingDays, insertWeekdayWindows]
  );

  const handleSingleInsert = useCallback(
    async (tempDateFrom: string | null, tempDateTo: string | null) => {
      if (!state.selectedSlotId) return;
      const weekdayWindowId = await ensureWeekdayWindowExists(
        state.selectedSlotId,
        (state.selectedDayIndex ?? 0) + 1
      );
      if (!weekdayWindowId || !state.selectedDistricts?.length) return;

      const objects = createDistrictObjects(
        [weekdayWindowId],
        state.selectedDistricts,
        tempDateFrom,
        tempDateTo
      );
      if (objects.length > 0) {
        await insertWeekdayWindowDistricts({ objects });
      }
    },
    [
      state.selectedSlotId,
      state.selectedDayIndex,
      state.selectedDistricts,
      ensureWeekdayWindowExists,
      createDistrictObjects,
      insertWeekdayWindowDistricts,
    ]
  );

  const processWeekdayWindowsInsert = useCallback(
    async (
      updatedWindows: GetWeekdayWindowForSlotQuery | GetWeekdayWindowsAllQuery,
      tempDateFrom: string | null,
      tempDateTo: string | null
    ) => {
      if (!updatedWindows?.weekday_window || !state.selectedDistricts?.length) return;

      const allowedWeekdays =
        tempDateFrom && tempDateTo && tempDateRange
          ? getWeekdaysInDateRange(tempDateRange[0], tempDateRange[1])
          : null;

      const filteredWindows = allowedWeekdays
        ? updatedWindows.weekday_window.filter(
            (
              w: NonNullable<
                | GetWeekdayWindowForSlotQuery['weekday_window']
                | GetWeekdayWindowsAllQuery['weekday_window']
              >[0]
            ) => w.weekday != null && allowedWeekdays.includes(w.weekday)
          )
        : updatedWindows.weekday_window;

      const weekdayWindowIds = filteredWindows.map(
        (
          w: NonNullable<
            | GetWeekdayWindowForSlotQuery['weekday_window']
            | GetWeekdayWindowsAllQuery['weekday_window']
          >[0]
        ) => w.id
      );
      const objects = createDistrictObjects(
        weekdayWindowIds,
        state.selectedDistricts,
        tempDateFrom,
        tempDateTo
      );
      if (objects.length > 0) {
        await insertWeekdayWindowDistricts({ objects });
      }
    },
    [state.selectedDistricts, tempDateRange, createDistrictObjects, insertWeekdayWindowDistricts]
  );

  const handleWeekInsert = useCallback(
    async (tempDateFrom: string | null, tempDateTo: string | null) => {
      if (!state.selectedSlotId) return;
      const weekdayWindowsForSlot = await weekdayWindowsForSlotRefetch();
      const weekdayWindowsData = weekdayWindowsForSlot.data as
        | GetWeekdayWindowForSlotQuery
        | undefined;

      const updatedWindows = await ensureWeekdayWindowsForDays(
        state.selectedSlotId,
        weekdayWindowsData
      );

      if (updatedWindows) {
        await processWeekdayWindowsInsert(updatedWindows, tempDateFrom, tempDateTo);
      }
    },
    [
      state.selectedSlotId,
      weekdayWindowsForSlotRefetch,
      ensureWeekdayWindowsForDays,
      processWeekdayWindowsInsert,
    ]
  );

  const handleColumnInsert = useCallback(
    async (tempDateFrom: string | null, tempDateTo: string | null) => {
      const allWeekdayWindows: Weekday_Window[] = [];
      const weekday = (state.selectedDayIndex ?? 0) + 1;

      for (const slot of state.timeSlots) {
        const weekdayWindow = await ensureWeekdayWindowExists(slot.id, weekday);
        if (weekdayWindow) {
          const existingWindow = slot.weekday_windows.find((ww) => ww.weekday === weekday);
          if (existingWindow) {
            allWeekdayWindows.push(existingWindow as Weekday_Window);
          } else {
            allWeekdayWindows.push({ id: weekdayWindow, weekday } as Weekday_Window);
          }
        }
      }

      if (allWeekdayWindows.length === 0 || !state.selectedDistricts?.length) return;

      const weekdayWindowIds = allWeekdayWindows.map((ww) => ww.id);
      const objects = createDistrictObjects(
        weekdayWindowIds,
        state.selectedDistricts,
        tempDateFrom,
        tempDateTo
      );
      if (objects.length > 0) {
        await insertWeekdayWindowDistricts({ objects });
      }
    },
    [
      state.timeSlots,
      state.selectedDayIndex,
      state.selectedDistricts,
      ensureWeekdayWindowExists,
      createDistrictObjects,
      insertWeekdayWindowDistricts,
    ]
  );

  const handleAllInsert = useCallback(
    async (tempDateFrom: string | null, tempDateTo: string | null) => {
      if (!state.selectedSlotId) return;
      const weekDayWindowsAll = await weekDayWindowsAllRefetch();
      const weekdayWindowsData = weekDayWindowsAll.data as GetWeekdayWindowsAllQuery | undefined;

      const updatedWindows = await ensureWeekdayWindowsForDays(
        state.selectedSlotId,
        weekdayWindowsData
      );

      if (updatedWindows) {
        await processWeekdayWindowsInsert(updatedWindows, tempDateFrom, tempDateTo);
      }
    },
    [
      state.selectedSlotId,
      weekDayWindowsAllRefetch,
      ensureWeekdayWindowsForDays,
      processWeekdayWindowsInsert,
    ]
  );

  const checkDistrictExists = useCallback(
    (data: GetDriversDistrictListByIdQuery | undefined, districtId: string) => {
      return (
        data?.user_by_pk?.driver_districts.some(
          (dd: NonNullable<GetDriversDistrictListByIdQuery['user_by_pk']>['driver_districts'][0]) =>
            dd.district?.id === districtId
        ) ?? false
      );
    },
    []
  );

  const addMissingDistricts = useCallback(
    async (driverId: string, districtIds: string[]) => {
      const { data } = await getDriverDistrictsListById.refetch();
      if (!data || !districtIds.length) return;

      for await (const districtId of districtIds) {
        const exists = checkDistrictExists(data, districtId);
        if (!exists) {
          await insertDriverDistrictOld.mutateAsync({ driverId, districtId });
        }
      }
    },
    [getDriverDistrictsListById, checkDistrictExists, insertDriverDistrictOld]
  );

  const cleanupAfterInsert = useCallback(async () => {
    const driverId = state.selectedDriverId ?? '';
    if (state.selectedDistricts && state.selectedDistricts.length > 0) {
      await addMissingDistricts(driverId, state.selectedDistricts);
    }
    setSelectedDistricts([]);
    close();
  }, [
    state.selectedDriverId,
    state.selectedDistricts,
    addMissingDistricts,
    setSelectedDistricts,
    close,
  ]);

  const isValidForDistrictAdd = useCallback(() => {
    return (
      state.selectedSlotId &&
      Boolean(state.selectedDistricts?.length) &&
      state.selectedDayIndex !== null
    );
  }, [state.selectedSlotId, state.selectedDistricts, state.selectedDayIndex]);

  const getTempDateRange = useCallback(() => {
    if (!isTempDistrict || !tempDateRange) return { tempDateFrom: null, tempDateTo: null };
    return {
      tempDateFrom: `${tempDateRange[0].format('YYYY-MM-DD')}T00:00:00`,
      tempDateTo: `${tempDateRange[1].format('YYYY-MM-DD')}T00:00:00`,
    };
  }, [isTempDistrict, tempDateRange]);

  const executeInsertByType = useCallback(
    async (tempDateFrom: string | null, tempDateTo: string | null) => {
      const insertHandlers = {
        [DistrictInsertType.SINGLE]: handleSingleInsert,
        [DistrictInsertType.WEEK]: handleWeekInsert,
        [DistrictInsertType.COLUMN]: handleColumnInsert,
        [DistrictInsertType.ALL]: handleAllInsert,
      };

      const handler = insertHandlers[state.districtInsertType];
      if (handler) {
        await handler(tempDateFrom, tempDateTo);
      }
    },
    [
      state.districtInsertType,
      handleSingleInsert,
      handleWeekInsert,
      handleColumnInsert,
      handleAllInsert,
    ]
  );

  const handleDistrictAdd = async () => {
    if (!isValidForDistrictAdd()) return;

    try {
      const { tempDateFrom, tempDateTo } = getTempDateRange();
      await executeInsertByType(tempDateFrom, tempDateTo);
      message.success('District(s) added successfully');
    } catch (error) {
      const errorMessage = formatError(error, 'Add Districts');
      logError(error, 'Add Districts');
      message.error(`Failed to add district(s): ${errorMessage}`);
    } finally {
      await cleanupAfterInsert();
    }
  };

  const onLoad = (map: google.maps.Map) => {
    mapRef.current = map;
  };

  const onUnmount = () => {
    mapRef.current = null;
  };

  return (
    <AppModal
      title={`Select District for ${modalTitle}`}
      open={open}
      destroyOnHidden={true}
      onOk={async () => {
        await handleDistrictAdd();
        reset();
      }}
      onCancel={() => {
        reset();
        close();
      }}
      okButtonProps={{
        loading:
          insertWeekdayWindowDistrictsLoading ||
          insertWeekdayWindowsLoading ||
          insertDriverDistrictOld.isPending,
      }}
      cancelButtonProps={{
        disabled: insertWeekdayWindowDistrictsLoading || insertWeekdayWindowsLoading,
      }}
    >
      <Select
        style={{ width: '100%' }}
        placeholder="Select districts"
        onChange={handleDistrictSelect}
        value={state.selectedDistricts}
        loading={driverDistrictsDataLoading}
        showSearch
        mode="multiple"
        optionFilterProp="label"
        tagRender={(props) => {
          const { closable, onClose, value } = props;
          const district = driversDistrictsData?.district?.find((d) => d.id === value);

          return (
            <Tag
              color={district?.districts_coordinates.length ? 'green' : 'red'}
              closable={closable}
              onClose={onClose}
              style={{ marginRight: 3 }}
            >
              {district?.name}
            </Tag>
          );
        }}
      >
        {driversDistrictsData?.district?.map((district) => {
          return (
            <Select.Option key={district.id} value={district.id} label={district.name}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Tag color={district.districts_coordinates.length ? 'green' : 'red'}>
                  {district.name}
                </Tag>
                <ScheduleModal districtName={district?.name || ''} districtId={district.id} />
              </div>
            </Select.Option>
          );
        })}
      </Select>

      {isLoaded && (
        <GoogleMap
          onLoad={onLoad}
          onUnmount={onUnmount}
          mapContainerClassName="google-map-container"
          zoom={1}
        >
          {selectedPolygons?.map((polygon) => (
            <Polygon
              key={polygon.name}
              paths={polygon.coordinates}
              options={{
                fillColor: 'red',
                fillOpacity: 0.3,
                strokeColor: 'red',
                strokeOpacity: 1,
                strokeWeight: 0,
              }}
            />
          ))}
        </GoogleMap>
      )}

      <div style={{ marginTop: 10 }}>
        <Radio.Group
          onChange={(e) => setDistrictInsertType(e.target.value)}
          value={state.districtInsertType}
        >
          {!isTempDistrict && (
            <Tooltip
              title="Adds the district only to the selected slot inside time slot"
              placement="top"
            >
              <Radio value={DistrictInsertType.SINGLE}>Slot</Radio>
            </Tooltip>
          )}
          <Tooltip title="Adds the district for the entire time slot (horizontal)" placement="top">
            <Radio value={DistrictInsertType.WEEK}>Time Slot</Radio>
          </Tooltip>
          {!isTempDistrict && (
            <Tooltip title="Adds the district for the entire day (vertical)" placement="top">
              <Radio value={DistrictInsertType.COLUMN}>Entire day</Radio>
            </Tooltip>
          )}
          <Tooltip title="Adds the district to the entire schedule" placement="top">
            <Radio value={DistrictInsertType.ALL}>Entire Schedule</Radio>
          </Tooltip>
        </Radio.Group>
      </div>
      <div style={{ marginTop: 10 }}>
        <Checkbox
          checked={isTempDistrict}
          onChange={(e) => {
            const checked = e.target.checked;
            if (checked) {
              setSelectedDistricts(null);
            }
            setIsTempDistrict(checked);
          }}
        >
          Temporal District
        </Checkbox>
        {isTempDistrict && (
          <DatePicker.RangePicker
            style={{ width: '100%', marginTop: 10 }}
            value={tempDateRange}
            onChange={handleDateRangeChange}
            disabledDate={disabledPastDates}
          />
        )}
      </div>
    </AppModal>
  );
};
